// istanbul ignore file
import { Injectable } from '@angular/core';
import { ConnectService } from '../connect/connect.service';
import { TermsConditionsResult } from '../../models/terms-conditions.model';
import { TranslateConfigService } from '../translate/translate-config.service';
import { StorageService } from '../storage/storage.service';
import { Observable, BehaviorSubject, filter } from 'rxjs';

const TERMS_AND_CONDITIONS_STORAGE_KEY = 'termsConditions';
@Injectable({
  providedIn: 'root',
})
export class TermsConditionsService {
  constructor(
    public connectSvc: ConnectService,
    private translateConfigService: TranslateConfigService,
    private storageSvc: StorageService
  ) {}

  get locale(): Promise<string> {
    return this.translateConfigService.getLanguage();
  }
  async loadContent() {
    return (await this.loadContentFromStorage(await this.locale)) || (await this.loadContentFromBackend());
  }

  content(): Observable<TermsConditionsResult> {
    const value = new BehaviorSubject(null);
    const observable = value.asObservable();

    const observer = async () => {
      const cachedContent = await this.loadContentFromStorage(await this.locale);
      if (cachedContent) {
        value.next(cachedContent);
      }

      await this.loadContentFromBackend();
      value.next(await this.loadContentFromStorage(await this.locale));
      value.complete();
    };

    observer();

    return observable.pipe(filter(val => val));
  }

  async loadContentFromBackend() {
    const locale = await this.locale;

    return new Promise<TermsConditionsResult>(resolve => {
      this.connectSvc.getCustom(`/current?locale=${locale}`).subscribe(
        (termsConditions: TermsConditionsResult) => {
          termsConditions.lang = locale;
          this.storageSvc.set(TERMS_AND_CONDITIONS_STORAGE_KEY, termsConditions);
          resolve(termsConditions);
        },

        error => {
          this.connectSvc.handleError(error);
          resolve(null);
        }
      );
    });
  }

  async loadContentFromStorage(locale: string) {
    const obj = await this.storageSvc.get(TERMS_AND_CONDITIONS_STORAGE_KEY);
    if (obj && obj.lang === locale) {
      return obj;
    }
    return null;
  }

  async clearContentFromStorage() {
    return await this.storageSvc.remove(TERMS_AND_CONDITIONS_STORAGE_KEY);
  }
}
